import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Waves,
  Bed,
  MapPin,
  Phone,
  EnvelopeSimple,
  List,
  X,
  FacebookLogo,
  InstagramLogo,
  TwitterLogo,
  WifiHigh,
  ForkKnife,
  Snowflake,
  Island,
  Anchor,
  Fish,
  Compass,
  Binoculars,
  CaretDown,
  CaretUp,
  ArrowRight,
  CheckCircle,
  Funnel,
  Export,
  Trash,
  Eye,
  Tag,
  Percent,
  Images,
  Lock,
  User,
} from "@phosphor-icons/react";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BOOKING_URL = "https://hippocampus.bookworldhostels.com/#angebot";

// Site Data
const SITE_DATA = {
  name: "Hippocampus Beach Resort",
  tagline: "Your Island Escape in Malapascua",
  description: "Located in the middle of Bounty Beach, Malapascua Island. Central to all restaurants, diving, and more!",
  address: "Bounty Beach, Malapascua Island, Daanbantayan, Cebu, Philippines 6013",
  phone: "+63 917 177 0500",
  phone2: "+63 917 137 8692",
  email: "hippocebupanalo1234@gmail.com",
  email2: "cebu.hippocampus@yahoo.com",
  coordinates: { lat: 11.3319, lng: 124.1136 },
  social: {
    facebook: "https://facebook.com/hippocampusbeachresort",
    instagram: "https://instagram.com/hippocampusbeachresort",
    twitter: "https://twitter.com/hippocampus"
  }
};

const ROOMS = [
  {
    id: "standard-ac",
    name: "Standard AC Room",
    shortDesc: "Garden facing, comfortable and affordable",
    description: "Garden facing rooms with window-type AC units, hot showers, safety box, and veranda area. Rooms feature either two twin beds or one big bed. Good for two pax with breakfast included.",
    occupancy: "1-3 guests (max)",
    price: "₱2,600",
    priceNote: "per night, includes breakfast for 2 pax",
    extraPerson: "Extra person: ₱850 with mattress & breakfast",
    amenities: ["Window-type AC", "Hot Shower", "Safety Box", "Veranda"],
    images: [
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/6z0w1e9e_Standard1.jpeg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/zrbk76if_standard2.jpeg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/iwuaqjw8_standard3.jpeg",
    ]
  },
  {
    id: "superior-ac",
    name: "Superior AC Room",
    shortDesc: "Garden facing, closer to beach",
    description: "Garden facing rooms, closer to beach than Standard rooms. Bigger rooms with split type AC units, high pressure hot showers, safety box, mini fridge, and veranda area. Two twin beds or one big bed. Good for two pax with breakfast included.",
    occupancy: "1-3 guests (max)",
    price: "₱3,600",
    priceNote: "per night, includes breakfast for 2 pax",
    extraPerson: "Extra person: ₱850 with mattress & breakfast",
    amenities: ["Split-type AC", "High Pressure Hot Shower", "Safety Box", "Mini Fridge", "Veranda"],
    images: [
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/g60fd9gi_Sup.jpeg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/gvnkui0b_Sup1.jpeg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/9heisyt0_Sup2.jpeg",
    ]
  },
  {
    id: "deluxe-seaview",
    name: "Deluxe Seaview Room",
    shortDesc: "Located directly at the beach!",
    description: "Our premium rooms located directly at the beach! Features split type, silent AC units, high pressure hot showers, safety box, mini fridge, and veranda area with table and chairs. Two twin beds or one big bed. Good for two pax with breakfast included.",
    occupancy: "1-3 guests (max)",
    price: "₱4,800",
    priceNote: "per night, includes breakfast for 2 pax",
    extraPerson: "Extra person: ₱850 with mattress & breakfast",
    amenities: ["Silent Split-type AC", "High Pressure Hot Shower", "Safety Box", "Mini Fridge", "Veranda with Table & Chairs", "Beach View"],
    images: [
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/84py3ek9_Deluxe.jpeg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/rhlsjpiu_Deluxe1.jpeg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/4frsqsc8_Deluxe2.jpeg",
    ]
  }
];

const ACTIVITIES = [
  {
    id: "scuba-diving",
    name: "Scuba Diving with Devocean Divers",
    shortDesc: "World-famous thresher shark diving experience",
    description: "Malapascua is the only place in the world where you can see thresher sharks almost every day. Our resort offers a well-respected dive centre located directly on the beach - Devocean Divers Malapascua, a distinguished 5-star PADI dive centre recognised for its professionalism and dedication to safety.",
    fullDescription: `Our resort offers a well-respected dive centre located directly on the beach. Devocean Divers Malapascua is a distinguished 5-star PADI dive centre recognised for its professionalism and dedication to safety. The facility provides high-quality amenities and is managed by an experienced international and local team committed to delivering attentive and skilled service to certified divers.

For those seeking a distinctive dive-island holiday experience, Malapascua, Philippines provides an outstanding destination. At Devocean Divers, there is a dual commitment: both to the preservation of the marine environment and to delivering memorable dive holidays for every guest.

Devocean Divers boasts a highly qualified team, with many staff members originating from the island itself. The local dive guides possess extensive knowledge of the region's reefs and marine life. Most PADI instructors are local, complemented by an international teaching staff who can conduct instruction in several languages. The team is passionate about diving education and capable of guiding guests through entry-level courses up to advanced certifications, including PADI professional levels.

Special rates for dive groups are available upon request.`,
    email: "malapascua@devoceandivers.com",
    images: [
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/753fw5bt_024%20%287%29.jpg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/gpl87uqd_024%20%282%29.jpg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/0w0a6zc2_019%20%283%29.jpg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/9gokj1i5_021%20%287%29.jpg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/q72b55ke_020%20%284%29.jpg",
    ]
  },
  {
    id: "island-hopping",
    name: "Kalanggaman Island",
    shortDesc: "Visit the stunning sandbar paradise",
    description: "Take a day trip to one of the most beautiful islands in the Philippines, famous for its pristine white sandbar stretching into crystal clear waters.",
    images: [
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/i56dtps5_WhatsApp%20Image%202026-04-14%20at%201.33.58%20PM.jpeg",
      "https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=1080&auto=format&fit=crop",
    ]
  },
  {
    id: "local-tours",
    name: "Local Tours",
    shortDesc: "Explore the island and meet friendly locals",
    description: "Experience the local villages and meet the friendly locals who call Malapascua home. Walk through coconut groves and discover hidden beaches.",
    images: [
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/2v8gugxh_Food%20or%20Grounds.jpg",
      "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/e6jk4xdt_Agoda%20Main%20image.jpg",
    ]
  },
  {
    id: "snorkeling",
    name: "Snorkeling",
    shortDesc: "Discover vibrant coral reefs",
    description: "Explore the colorful underwater world right off our beach. Perfect for beginners and those who want to enjoy marine life without diving deep.",
    images: [
      "https://images.unsplash.com/photo-1637804438194-a638908197b2?q=85&fm=jpg&crop=entropy&cs=srgb&w=1080",
      "https://images.unsplash.com/photo-1641435039819-cf9a31d893c2?q=85&fm=jpg&crop=entropy&cs=srgb&w=1080",
    ]
  },
  {
    id: "sunset-cruise",
    name: "Sunset Cruise",
    shortDesc: "Magical evenings on the water",
    description: "End your perfect day with a relaxing boat ride as the sun sets over the Philippine Sea. Includes refreshments and unforgettable views.",
    images: [
      "https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=1080&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?q=80&w=1080&auto=format&fit=crop",
    ]
  }
];

const AMENITIES = [
  { icon: Waves, name: "Beach Access", desc: "Direct access to Bounty Beach" },
  { icon: WifiHigh, name: "Free WiFi", desc: "Stay connected throughout" },
  { icon: ForkKnife, name: "Restaurant", desc: "Fresh local cuisine daily" },
  { icon: Anchor, name: "Diving Center", desc: "PADI certified courses" },
  { icon: Snowflake, name: "Air Conditioning", desc: "Cool comfort guaranteed" },
  { icon: Island, name: "Island Tours", desc: "Explore Malapascua & beyond" },
];

const FAQ_DATA = [
  {
    question: "What time is check-in and check-out?",
    answer: "Check-in is at 2:00 PM and check-out is at 11:00 AM. Early check-in or late check-out may be available upon request, subject to availability."
  },
  {
    question: "How do I get to Malapascua Island?",
    answer: "From Cebu City, take a bus to Maya Port (approximately 4 hours). From Maya, regular boats depart for Malapascua Island. The boat ride takes about 30 minutes. We can help arrange private transfers upon request."
  },
  {
    question: "Is breakfast included?",
    answer: "Yes, all room rates include a basic breakfast for each guest. Additional breakfast options are available at our restaurant."
  },
  {
    question: "Do you accept credit cards?",
    answer: "We accept credit cards with a processing fee. Payment can also be made via bank transfer (Philippine or German accounts) or PayPal. Cash payments are accepted on-site."
  },
  {
    question: "What is your cancellation policy?",
    answer: "Cancellations made 15+ days before arrival receive a full refund. Cancellations 7-15 days before arrival forfeit 50% of the deposit. Cancellations less than 7 days before arrival are charged in full."
  },
  {
    question: "Can I see thresher sharks year-round?",
    answer: "Yes! Malapascua is unique because thresher sharks visit the cleaning stations near Monad Shoal almost every day of the year. Early morning dives offer the best chances."
  }
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/rooms", label: "Rooms" },
    { path: "/activities", label: "Activities" },
    { path: "/gallery", label: "Gallery" },
    { path: "/about", label: "About" },
    { path: "/faq", label: "FAQ" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#F4F1EA]/85 border-b border-[#0A2530]/10" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Waves size={32} weight="light" className="text-ocean" />
          <span className="font-heading text-xl font-semibold text-ocean hidden sm:block">Hippocampus</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-coral ${
                location.pathname === link.path ? "text-coral" : "text-ocean"
              }`}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#0A2530] text-[#F4F1EA] hover:bg-[#163A48] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
            data-testid="book-now-btn"
          >
            Book Now
          </a>
          <button
            className="md:hidden text-ocean"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#F4F1EA] border-b border-[#DCD7CE]"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-medium ${
                    location.pathname === link.path ? "text-coral" : "text-ocean"
                  }`}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-[#0A2530] text-[#F4F1EA] py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Waves size={32} weight="light" />
            <span className="font-heading text-2xl">Hippocampus</span>
          </div>
          <p className="text-[#8A9699] font-light leading-relaxed">
            Your home away from home since 2005. Experience world-class diving and island hospitality on Malapascua.
          </p>
        </div>
        
        <div>
          <h4 className="font-heading text-lg mb-4">Quick Links</h4>
          <div className="flex flex-col gap-2">
            {["Rooms", "Activities", "About", "FAQ", "Contact"].map((link) => (
              <Link
                key={link}
                to={`/${link.toLowerCase()}`}
                className="text-[#8A9699] hover:text-[#F4F1EA] transition-colors text-sm"
              >
                {link}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-heading text-lg mb-4">Contact</h4>
          <div className="flex flex-col gap-3 text-[#8A9699] text-sm">
            <div className="flex items-start gap-2">
              <MapPin size={18} className="flex-shrink-0 mt-0.5" />
              <span>{SITE_DATA.address}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={18} />
              <div className="flex flex-col">
                <span>{SITE_DATA.phone}</span>
                <span>{SITE_DATA.phone2}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={18} />
              <div className="flex flex-col">
                <span>{SITE_DATA.email}</span>
                <span>{SITE_DATA.email2}</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-heading text-lg mb-4">Follow Us</h4>
          <div className="flex gap-4">
            <a href={SITE_DATA.social.facebook} target="_blank" rel="noopener noreferrer" className="text-[#8A9699] hover:text-[#F4F1EA] transition-colors" data-testid="social-facebook">
              <FacebookLogo size={24} />
            </a>
            <a href={SITE_DATA.social.instagram} target="_blank" rel="noopener noreferrer" className="text-[#8A9699] hover:text-[#F4F1EA] transition-colors" data-testid="social-instagram">
              <InstagramLogo size={24} />
            </a>
            <a href={SITE_DATA.social.twitter} target="_blank" rel="noopener noreferrer" className="text-[#8A9699] hover:text-[#F4F1EA] transition-colors" data-testid="social-twitter">
              <TwitterLogo size={24} />
            </a>
          </div>
          <div className="mt-6">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3893.5!2d${SITE_DATA.coordinates.lng}!3d${SITE_DATA.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTHCsDE5JzU0LjgiTiAxMjTCsDA2JzQ5LjAiRQ!5e0!3m2!1sen!2sph!4v1600000000000!5m2!1sen!2sph`}
              width="100%"
              height="120"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              className="map-filter"
              title="Resort Location"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-[#163A48] pt-8 text-center text-[#8A9699] text-sm">
        <p>&copy; {new Date().getFullYear()} Hippocampus Beach Resort. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Hero Section Component
const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroImages = [
    "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/e6jk4xdt_Agoda%20Main%20image.jpg",
    "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/4frsqsc8_Deluxe2.jpeg",
    "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/qdvozls3_Bar%202.jpg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <section className="relative h-screen" data-testid="hero-section">
      {heroImages.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={img}
            alt={`Malapascua beach scene ${index + 1}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2530]/60 via-transparent to-black/20" />
        </div>
      ))}

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center px-6 max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4"
          >
            Malapascua Island, Philippines
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-heading text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[1.1] text-white mb-6"
          >
            {SITE_DATA.tagline}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg text-white/80 font-light mb-10 max-w-xl mx-auto"
          >
            Experience world-class thresher shark diving and tropical island paradise at Bounty Beach
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#A85239] text-white hover:bg-[#8C432E] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
              data-testid="hero-book-room-btn"
            >
              Book a Room
            </a>
            <Link
              to="/activities"
              className="border border-white text-white hover:bg-white hover:text-[#0A2530] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-all"
              data-testid="hero-explore-activities-btn"
            >
              Explore Activities
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 transition-all ${
              index === currentSlide ? "bg-white w-8" : "bg-white/50"
            }`}
            data-testid={`hero-slide-${index}`}
          />
        ))}
      </div>
    </section>
  );
};

// Marquee Component
const MarqueeSection = () => (
  <div className="bg-[#0A2530] py-4" data-testid="marquee-section">
    <Marquee gradient={false} speed={40} className="text-[#F4F1EA]">
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">
        Malapascua Island
      </span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">•</span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">
        Thresher Shark Diving
      </span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">•</span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">
        Luxury Escape
      </span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">•</span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">
        Your Home Away From Home
      </span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">•</span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">
        Est. 2005
      </span>
      <span className="mx-8 text-sm tracking-[0.2em] uppercase font-light">•</span>
    </Marquee>
  </div>
);

// About Section Component
const AboutSection = () => (
  <section className="py-24 md:py-32 px-6" data-testid="about-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Since 2005
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-ocean mb-6">
            Your Home Away From Home
          </h2>
          <p className="text-base sm:text-lg font-light leading-relaxed text-[#4A5D62] mb-6">
            {SITE_DATA.description}
          </p>
          <p className="text-base sm:text-lg font-light leading-relaxed text-[#4A5D62] mb-8">
            Our native style, unique resort is designed to make you feel at home while experiencing the natural beauty of Malapascua. From world-class diving to peaceful beach relaxation, we offer everything you need for an unforgettable island escape.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-ocean font-medium hover:text-coral transition-colors"
            data-testid="about-read-more"
          >
            Read Our Story <ArrowRight size={18} />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="image-hover-zoom"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/2v8gugxh_Food%20or%20Grounds.jpg"
            alt="Breakfast at Hippocampus Beach Resort"
            className="w-full h-[500px] object-cover"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

// Rooms Preview Section
const RoomsSection = () => (
  <section className="py-24 md:py-32 px-6 bg-white" data-testid="rooms-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
          Accommodations
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-ocean">
          Our Rooms
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {ROOMS.map((room, index) => (
          <motion.div
            key={room.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="group bg-white border border-[#DCD7CE] shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            data-testid={`room-card-${room.id}`}
          >
            <div className="image-hover-zoom aspect-[4/3]">
              <img
                src={room.images[0]}
                alt={room.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6">
              <h3 className="font-heading text-2xl font-normal text-ocean mb-2">
                {room.name}
              </h3>
              <p className="text-[#4A5D62] font-light text-sm mb-2">
                {room.shortDesc}
              </p>
              <p className="text-coral font-semibold text-lg mb-1">{room.price}</p>
              <p className="text-xs text-[#8A9699] mb-4">{room.priceNote}</p>
              <div className="flex gap-4">
                <Link
                  to={`/rooms#${room.id}`}
                  className="flex-1 border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA] px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-all text-center"
                  data-testid={`view-room-${room.id}`}
                >
                  View Details
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-12">
        <Link
          to="/rooms"
          className="inline-flex items-center gap-2 bg-[#0A2530] text-[#F4F1EA] hover:bg-[#163A48] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
          data-testid="view-all-rooms-btn"
        >
          View All Rooms <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

// Amenities Section
const AmenitiesSection = () => (
  <section className="py-24 md:py-32 px-6 bg-[#EAE6DF]" data-testid="amenities-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
          Resort Features
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-ocean">
          Amenities
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {AMENITIES.map((amenity, index) => (
          <motion.div
            key={amenity.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="text-center p-6"
            data-testid={`amenity-${amenity.name.toLowerCase().replace(/\s/g, '-')}`}
          >
            <amenity.icon size={48} weight="light" className="mx-auto text-ocean mb-4" />
            <h4 className="font-medium text-ocean mb-1">{amenity.name}</h4>
            <p className="text-sm text-[#4A5D62] font-light">{amenity.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Activities Preview Section
const ActivitiesSection = () => (
  <section className="py-24 md:py-32 px-6" data-testid="activities-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
          Experiences
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-ocean">
          Activities
        </h2>
      </div>

      {/* Asymmetric Tetris Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Large card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="md:col-span-7 md:row-span-2"
          data-testid={`activity-card-${ACTIVITIES[0].id}`}
        >
          <div className="relative h-full min-h-[400px] md:min-h-[600px] group image-hover-zoom">
            <img
              src={ACTIVITIES[0].images[0]}
              alt={ACTIVITIES[0].name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h3 className="font-heading text-3xl text-white mb-2">{ACTIVITIES[0].name}</h3>
              <p className="text-white/80 font-light mb-4">{ACTIVITIES[0].shortDesc}</p>
              <Link
                to={`/activities#${ACTIVITIES[0].id}`}
                className="inline-flex items-center gap-2 text-white font-medium hover:text-coral transition-colors"
                data-testid={`view-activity-${ACTIVITIES[0].id}`}
              >
                View Details <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Smaller cards */}
        {ACTIVITIES.slice(1, 3).map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 * (index + 1) }}
            className="md:col-span-5"
            data-testid={`activity-card-${activity.id}`}
          >
            <div className="relative h-[280px] group image-hover-zoom">
              <img
                src={activity.images[0]}
                alt={activity.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-2xl text-white mb-2">{activity.name}</h3>
                <p className="text-white/80 font-light text-sm mb-3">{activity.shortDesc}</p>
                <Link
                  to={`/activities#${activity.id}`}
                  className="inline-flex items-center gap-2 text-white text-sm font-medium hover:text-coral transition-colors"
                  data-testid={`view-activity-${activity.id}`}
                >
                  View Details <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-12">
        <Link
          to="/activities"
          className="inline-flex items-center gap-2 bg-[#0A2530] text-[#F4F1EA] hover:bg-[#163A48] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
          data-testid="view-all-activities-btn"
        >
          View All Activities <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

// Booking Inquiry Section
const BookingInquirySection = () => {
  const [activeTab, setActiveTab] = useState("room");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const endpoint = activeTab === "room" ? "/inquiries/room" : "/inquiries/activity";
      await axios.post(`${API}${endpoint}`, formData);
      setSuccess(true);
      setFormData({});
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="py-24 md:py-32 px-6 bg-white" data-testid="booking-inquiry-section">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle size={64} className="mx-auto text-green-600 mb-6" />
          <h2 className="font-heading text-3xl text-ocean mb-4">Thank You!</h2>
          <p className="text-[#4A5D62] mb-8">
            Your inquiry has been received. We'll get back to you within 24 hours.
          </p>
          <button
            onClick={() => {
              setSuccess(false);
              setFormData({});
            }}
            className="bg-[#0A2530] text-[#F4F1EA] hover:bg-[#163A48] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
            data-testid="submit-another-btn"
          >
            Submit Another Inquiry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-32 px-6 bg-white" data-testid="booking-inquiry-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Make a Reservation
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-ocean">
            Booking Inquiry
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => {
              setActiveTab("room");
              setFormData({});
              setError("");
            }}
            className={`px-8 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-all ${
              activeTab === "room"
                ? "bg-[#0A2530] text-[#F4F1EA]"
                : "border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA]"
            }`}
            data-testid="tab-room-booking"
          >
            Room Booking
          </button>
          <button
            onClick={() => {
              setActiveTab("activity");
              setFormData({});
              setError("");
            }}
            className={`px-8 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-all ${
              activeTab === "activity"
                ? "bg-[#0A2530] text-[#F4F1EA]"
                : "border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA]"
            }`}
            data-testid="tab-activity-booking"
          >
            Activity Booking
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name || ""}
                onChange={handleInputChange}
                required
                className="form-input-underline"
                placeholder="Your full name"
                data-testid="input-name"
              />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email || ""}
                onChange={handleInputChange}
                required
                className="form-input-underline"
                placeholder="your@email.com"
                data-testid="input-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                WhatsApp Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone || ""}
                onChange={handleInputChange}
                required
                className="form-input-underline"
                placeholder="+63 9XX XXX XXXX"
                data-testid="input-phone"
              />
            </div>

            {activeTab === "room" ? (
              <div>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                  Room Type *
                </label>
                <select
                  name="room_type"
                  value={formData.room_type || ""}
                  onChange={handleInputChange}
                  required
                  className="form-input-underline bg-transparent"
                  data-testid="select-room-type"
                >
                  <option value="">Select a room</option>
                  {ROOMS.map((room) => (
                    <option key={room.id} value={room.name}>
                      {room.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                  Activity Type *
                </label>
                <select
                  name="activity_type"
                  value={formData.activity_type || ""}
                  onChange={handleInputChange}
                  required
                  className="form-input-underline bg-transparent"
                  data-testid="select-activity-type"
                >
                  <option value="">Select an activity</option>
                  {ACTIVITIES.map((activity) => (
                    <option key={activity.id} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {activeTab === "room" ? (
              <>
                <div>
                  <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                    Check-in Date *
                  </label>
                  <input
                    type="date"
                    name="check_in_date"
                    value={formData.check_in_date || ""}
                    onChange={handleInputChange}
                    required
                    className="form-input-underline"
                    data-testid="input-checkin"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                    Check-out Date *
                  </label>
                  <input
                    type="date"
                    name="check_out_date"
                    value={formData.check_out_date || ""}
                    onChange={handleInputChange}
                    required
                    className="form-input-underline"
                    data-testid="input-checkout"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    name="preferred_date"
                    value={formData.preferred_date || ""}
                    onChange={handleInputChange}
                    required
                    className="form-input-underline"
                    data-testid="input-preferred-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                    Number of People *
                  </label>
                  <input
                    type="number"
                    name="num_people"
                    value={formData.num_people || ""}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="form-input-underline"
                    placeholder="How many people?"
                    data-testid="input-num-people"
                  />
                </div>
              </>
            )}
          </div>

          {activeTab === "room" && (
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                Number of Guests *
              </label>
              <input
                type="number"
                name="num_guests"
                value={formData.num_guests || ""}
                onChange={handleInputChange}
                required
                min="1"
                className="form-input-underline"
                placeholder="How many guests?"
                data-testid="input-num-guests"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
              Message (Optional)
            </label>
            <textarea
              name="message"
              value={formData.message || ""}
              onChange={handleInputChange}
              rows="4"
              className="form-input-underline resize-none"
              placeholder="Any special requests or questions?"
              data-testid="input-message"
            />
          </div>

          <div className="text-center">
            <button
              type="submit"
              disabled={loading}
              className="bg-[#A85239] text-white hover:bg-[#8C432E] px-12 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid="submit-inquiry-btn"
            >
              {loading ? "Sending..." : "Submit Inquiry"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

// Image Gallery Modal
const ImageGallery = ({ images, isOpen, onClose, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
        onClick={onClose}
        data-testid="image-gallery-modal"
      >
        <button
          className="absolute top-6 right-6 text-white hover:text-coral"
          onClick={onClose}
          data-testid="gallery-close-btn"
        >
          <X size={32} />
        </button>
        <div className="max-w-5xl w-full px-4" onClick={(e) => e.stopPropagation()}>
          <h3 className="font-heading text-2xl text-white text-center mb-6">{title}</h3>
          <img
            src={images[currentIndex]}
            alt={`${title} - Image ${currentIndex + 1}`}
            className="w-full h-auto max-h-[70vh] object-contain"
          />
          <div className="flex justify-center gap-2 mt-4">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 transition-all ${
                  index === currentIndex ? "bg-white" : "bg-white/30"
                }`}
                data-testid={`gallery-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// HomePage Component
const HomePage = () => (
  <>
    <HeroSection />
    <MarqueeSection />
    <AboutSection />
    <RoomsSection />
    <AmenitiesSection />
    <ActivitiesSection />
    <BookingInquirySection />
  </>
);

// RoomsPage Component
const RoomsPage = () => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  return (
    <div className="pt-24" data-testid="rooms-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img
          src="https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/rhlsjpiu_Deluxe1.jpeg"
          alt="Resort rooms"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2530]/60 via-transparent to-black/20" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
              Accommodations
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-light text-white">
              Our Rooms
            </h1>
          </div>
        </div>
      </section>

      {/* Room Cards */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto space-y-24">
          {ROOMS.map((room, index) => (
            <motion.div
              key={room.id}
              id={room.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
              data-testid={`room-detail-${room.id}`}
            >
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <div className="image-hover-zoom">
                  <img
                    src={room.images[0]}
                    alt={room.name}
                    className="w-full h-[400px] object-cover cursor-pointer"
                    onClick={() => {
                      setSelectedRoom(room);
                      setGalleryOpen(true);
                    }}
                  />
                </div>
              </div>
              <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
                  {room.occupancy}
                </p>
                <h2 className="font-heading text-4xl font-light text-ocean mb-2">
                  {room.name}
                </h2>
                <p className="text-coral font-semibold text-2xl mb-1">{room.price}</p>
                <p className="text-sm text-[#8A9699] mb-4">{room.priceNote}</p>
                <p className="text-[#4A5D62] font-light leading-relaxed mb-4">
                  {room.description}
                </p>
                <p className="text-sm text-coral mb-6">{room.extraPerson}</p>
                <div className="flex flex-wrap gap-4 mb-8">
                  {room.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="text-sm text-[#4A5D62] bg-[#EAE6DF] px-3 py-1"
                    >
                      {amenity}
                    </span>
                  ))}
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setSelectedRoom(room);
                      setGalleryOpen(true);
                    }}
                    className="border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-all"
                    data-testid={`gallery-btn-${room.id}`}
                  >
                    View Gallery
                  </button>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#A85239] text-white hover:bg-[#8C432E] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
                    data-testid={`book-btn-${room.id}`}
                  >
                    Book This Room
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <ImageGallery
        images={selectedRoom?.images || []}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={selectedRoom?.name || ""}
      />
    </div>
  );
};

// ActivitiesPage Component
const ActivitiesPage = () => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  return (
    <div className="pt-24" data-testid="activities-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img
          src="https://images.unsplash.com/photo-1641435039819-cf9a31d893c2?q=85&fm=jpg&crop=entropy&cs=srgb&w=1920"
          alt="Diving activities"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2530]/60 via-transparent to-black/20" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
              Experiences
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-light text-white">
              Activities
            </h1>
          </div>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Devocean Divers Feature Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-24 bg-[#0A2530] text-white p-8 md:p-12"
            data-testid="devocean-divers-section"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
                  5-Star PADI Dive Centre
                </p>
                <h2 className="font-heading text-4xl font-light mb-6">
                  Devocean Divers Malapascua
                </h2>
                <p className="text-[#8A9699] font-light leading-relaxed mb-4">
                  Our resort offers a well-respected dive centre located directly on the beach. Devocean Divers Malapascua is a distinguished 5-star PADI dive centre recognised for its professionalism and dedication to safety.
                </p>
                <p className="text-[#8A9699] font-light leading-relaxed mb-4">
                  For those seeking a distinctive dive-island holiday experience, Malapascua provides an outstanding destination. At Devocean Divers, there is a dual commitment: both to the preservation of the marine environment and to delivering memorable dive holidays for every guest.
                </p>
                <p className="text-[#8A9699] font-light leading-relaxed mb-6">
                  The team is passionate about diving education and capable of guiding guests through entry-level courses up to advanced certifications, including PADI professional levels. Special rates for dive groups are available upon request.
                </p>
                <a
                  href="mailto:malapascua@devoceandivers.com"
                  className="inline-flex items-center gap-2 bg-[#A85239] text-white hover:bg-[#8C432E] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
                  data-testid="devocean-email-btn"
                >
                  <EnvelopeSimple size={16} />
                  malapascua@devoceandivers.com
                </a>
              </div>
              <div className="image-hover-zoom">
                <img
                  src="https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/0w0a6zc2_019%20%283%29.jpg"
                  alt="Devocean Divers team"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ACTIVITIES.map((activity, index) => (
              <motion.div
                key={activity.id}
                id={activity.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-[#DCD7CE] shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                data-testid={`activity-detail-${activity.id}`}
              >
                <div className="image-hover-zoom aspect-[4/3]">
                  <img
                    src={activity.images[0]}
                    alt={activity.name}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => {
                      setSelectedActivity(activity);
                      setGalleryOpen(true);
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl font-normal text-ocean mb-2">
                    {activity.name}
                  </h3>
                  <p className="text-[#4A5D62] font-light text-sm mb-4">
                    {activity.shortDesc}
                  </p>
                  <p className="text-[#4A5D62] font-light text-sm mb-6 line-clamp-3">
                    {activity.description}
                  </p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setSelectedActivity(activity);
                        setGalleryOpen(true);
                      }}
                      className="flex-1 border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA] px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-all text-center"
                      data-testid={`view-images-${activity.id}`}
                    >
                      View Images
                    </button>
                    <Link
                      to="/#booking-inquiry"
                      className="flex-1 bg-[#A85239] text-white hover:bg-[#8C432E] px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-colors text-center"
                      data-testid={`book-activity-${activity.id}`}
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ImageGallery
        images={selectedActivity?.images || []}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={selectedActivity?.name || ""}
      />
    </div>
  );
};

// AboutPage Component
const AboutPage = () => (
  <div className="pt-24" data-testid="about-page">
    {/* Hero */}
    <section className="relative h-[60vh] min-h-[500px]">
      <img
        src="https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?q=80&w=1920&auto=format&fit=crop"
        alt="Tropical paradise"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A2530]/60 via-transparent to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Our Story
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-light text-white">
            About Us
          </h1>
        </div>
      </div>
    </section>

    {/* Content */}
    <section className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Est. 2005
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-ocean mb-8">
            Your Home Away From Home
          </h2>
          
          <div className="prose prose-lg max-w-none text-[#4A5D62] font-light leading-relaxed space-y-6">
            <p>
              Located in the heart of Bounty Beach on Malapascua Island, Hippocampus Beach Resort has been welcoming travelers from around the world since 2005. Our native-style resort offers the perfect blend of authentic Filipino hospitality and modern comfort.
            </p>
            <p>
              Malapascua Island is world-famous for being the only place on Earth where you can regularly see thresher sharks. Our dive center partners with local experts to offer unforgettable underwater experiences, from early morning shark dives to leisurely reef explorations.
            </p>
            <p>
              Beyond diving, our location at the center of Bounty Beach means you're steps away from pristine white sand, crystal-clear waters, and the charming local community that makes Malapascua so special.
            </p>
            <p>
              Whether you're here for the diving, the beaches, or simply to escape the everyday, our team is dedicated to making your stay memorable. We believe in sustainable tourism and work closely with the local community to preserve the natural beauty that makes this island paradise so unique.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
        >
          <div className="p-8 bg-[#EAE6DF]">
            <p className="font-heading text-5xl text-coral mb-2">2005</p>
            <p className="text-[#4A5D62] font-light">Established</p>
          </div>
          <div className="p-8 bg-[#EAE6DF]">
            <p className="font-heading text-5xl text-coral mb-2">15+</p>
            <p className="text-[#4A5D62] font-light">Rooms Available</p>
          </div>
          <div className="p-8 bg-[#EAE6DF]">
            <p className="font-heading text-5xl text-coral mb-2">1000s</p>
            <p className="text-[#4A5D62] font-light">Happy Guests</p>
          </div>
        </motion.div>
      </div>
    </section>
  </div>
);

// FAQPage Component
const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="pt-24" data-testid="faq-page">
      {/* Hero */}
      <section className="py-24 px-6 bg-[#EAE6DF]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Help Center
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-light text-ocean">
            Frequently Asked Questions
          </h1>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          {FAQ_DATA.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="border-b border-[#DCD7CE]"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full py-6 flex items-center justify-between text-left"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-heading text-xl text-ocean pr-4">
                  {faq.question}
                </span>
                {openIndex === index ? (
                  <CaretUp size={24} className="text-coral flex-shrink-0" />
                ) : (
                  <CaretDown size={24} className="text-ocean flex-shrink-0" />
                )}
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 text-[#4A5D62] font-light leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

// ContactPage Component
const ContactPage = () => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({});
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 px-6 bg-[#EAE6DF]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Get in Touch
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-light text-ocean">
            Contact Us
          </h1>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl text-ocean mb-8">
                Get in Touch
              </h2>
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <MapPin size={24} className="text-coral flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-ocean mb-1">Address</h4>
                    <p className="text-[#4A5D62] font-light">{SITE_DATA.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={24} className="text-coral flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-ocean mb-1">Phone</h4>
                    <p className="text-[#4A5D62] font-light">{SITE_DATA.phone}</p>
                    <p className="text-[#4A5D62] font-light">{SITE_DATA.phone2}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <EnvelopeSimple size={24} className="text-coral flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-ocean mb-1">Email</h4>
                    <p className="text-[#4A5D62] font-light">{SITE_DATA.email}</p>
                    <p className="text-[#4A5D62] font-light">{SITE_DATA.email2}</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mb-12">
                <h4 className="font-medium text-ocean mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <a href={SITE_DATA.social.facebook} target="_blank" rel="noopener noreferrer" className="text-ocean hover:text-coral transition-colors">
                    <FacebookLogo size={28} />
                  </a>
                  <a href={SITE_DATA.social.instagram} target="_blank" rel="noopener noreferrer" className="text-ocean hover:text-coral transition-colors">
                    <InstagramLogo size={28} />
                  </a>
                  <a href={SITE_DATA.social.twitter} target="_blank" rel="noopener noreferrer" className="text-ocean hover:text-coral transition-colors">
                    <TwitterLogo size={28} />
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="aspect-video">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7787!2d${SITE_DATA.coordinates.lng}!3d${SITE_DATA.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTHCsDE5JzU0LjgiTiAxMjTCsDA2JzQ5LjAiRQ!5e0!3m2!1sen!2sph!4v1600000000000!5m2!1sen!2sph`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  className="map-filter"
                  title="Resort Location"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl text-ocean mb-8">
                Send a Message
              </h2>

              {success ? (
                <div className="text-center py-12">
                  <CheckCircle size={64} className="mx-auto text-green-600 mb-6" />
                  <h3 className="font-heading text-2xl text-ocean mb-4">Message Sent!</h3>
                  <p className="text-[#4A5D62] mb-8">
                    We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="bg-[#0A2530] text-[#F4F1EA] hover:bg-[#163A48] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
                    data-testid="send-another-message-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3">
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name || ""}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="form-input-underline"
                      placeholder="Your full name"
                      data-testid="contact-input-name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email || ""}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="form-input-underline"
                      placeholder="your@email.com"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={formData.subject || ""}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="form-input-underline"
                      placeholder="What is this about?"
                      data-testid="contact-input-subject"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">
                      Message *
                    </label>
                    <textarea
                      value={formData.message || ""}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows="6"
                      className="form-input-underline resize-none"
                      placeholder="Your message..."
                      data-testid="contact-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#A85239] text-white hover:bg-[#8C432E] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Gallery Page Component - Placeholder for user's images
const GALLERY_IMAGES = [
  { id: 1, category: "resort", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/84py3ek9_Deluxe.jpeg", title: "Deluxe Seaview Room" },
  { id: 2, category: "rooms", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/6z0w1e9e_Standard1.jpeg", title: "Standard AC Room" },
  { id: 3, category: "diving", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/753fw5bt_024%20%287%29.jpg", title: "Thresher Shark" },
  { id: 4, category: "rooms", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/rhlsjpiu_Deluxe1.jpeg", title: "Deluxe Room with Ocean View" },
  { id: 5, category: "rooms", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/g60fd9gi_Sup.jpeg", title: "Superior AC Room" },
  { id: 6, category: "diving", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/gpl87uqd_024%20%282%29.jpg", title: "Dive Boat Trip" },
  { id: 7, category: "rooms", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/zrbk76if_standard2.jpeg", title: "Standard Room Interior" },
  { id: 8, category: "resort", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/iwuaqjw8_standard3.jpeg", title: "Resort Building" },
  { id: 9, category: "diving", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/0w0a6zc2_019%20%283%29.jpg", title: "Devocean Divers Team" },
  { id: 10, category: "diving", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/9gokj1i5_021%20%287%29.jpg", title: "Dive Preparation" },
  { id: 11, category: "diving", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/q72b55ke_020%20%284%29.jpg", title: "Happy Divers" },
  { id: 12, category: "resort", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/4frsqsc8_Deluxe2.jpeg", title: "Beach View from Veranda" },
  { id: 13, category: "rooms", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/gvnkui0b_Sup1.jpeg", title: "Superior Room Veranda" },
  { id: 14, category: "rooms", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/9heisyt0_Sup2.jpeg", title: "Superior Room Interior" },
  { id: 15, category: "resort", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/qdvozls3_Bar%202.jpg", title: "Beach Bar Happy Hour" },
  { id: 16, category: "resort", url: "https://customer-assets.emergentagent.com/job_hippo-escape/artifacts/2v8gugxh_Food%20or%20Grounds.jpg", title: "Breakfast by the Beach" },
];

const GalleryPage = () => {
  const [filter, setFilter] = useState("all");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = ["all", "resort", "rooms", "diving", "activities"];
  const filteredImages = filter === "all" 
    ? GALLERY_IMAGES 
    : GALLERY_IMAGES.filter(img => img.category === filter);

  return (
    <div className="pt-24" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-24 px-6 bg-[#EAE6DF]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A85239] mb-4">
            Photo Gallery
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-light text-ocean">
            Our Gallery
          </h1>
          <p className="mt-4 text-[#4A5D62] font-light">
            Explore the beauty of Hippocampus Beach Resort and Malapascua Island
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 px-6 border-b border-[#DCD7CE]">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-4">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-6 py-2 uppercase tracking-[0.15em] text-xs font-semibold transition-all ${
                filter === cat
                  ? "bg-[#0A2530] text-[#F4F1EA]"
                  : "border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA]"
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="image-hover-zoom cursor-pointer aspect-square"
                onClick={() => {
                  setSelectedImage(image);
                  setLightboxOpen(true);
                }}
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>
          
          {filteredImages.length === 0 && (
            <p className="text-center text-[#4A5D62] py-12">No images in this category yet.</p>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-coral"
              onClick={() => setLightboxOpen(false)}
            >
              <X size={32} />
            </button>
            <div className="max-w-5xl w-full px-4" onClick={(e) => e.stopPropagation()}>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              <p className="text-center text-white mt-4 font-heading text-xl">{selectedImage.title}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Admin Dashboard Component
const ADMIN_PASSWORD = "hippo2024";

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("room");
  const [inquiries, setInquiries] = useState({ room: [], activity: [], contact: [] });
  const [loading, setLoading] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ code: "", percentage: "", validUntil: "" });
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      fetchInquiries();
    } else {
      alert("Invalid password");
    }
  };

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const [roomRes, activityRes, contactRes] = await Promise.all([
        axios.get(`${API}/inquiries/room`),
        axios.get(`${API}/inquiries/activity`),
        axios.get(`${API}/contact`)
      ]);
      setInquiries({
        room: roomRes.data,
        activity: activityRes.data,
        contact: contactRes.data
      });
    } catch (err) {
      console.error("Failed to fetch inquiries", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDiscount = (e) => {
    e.preventDefault();
    if (newDiscount.code && newDiscount.percentage) {
      setDiscounts([...discounts, { ...newDiscount, id: Date.now() }]);
      setNewDiscount({ code: "", percentage: "", validUntil: "" });
    }
  };

  const handleDeleteDiscount = (id) => {
    setDiscounts(discounts.filter(d => d.id !== id));
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map(row => Object.values(row).join(",")).join("\n");
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center bg-[#F4F1EA]" data-testid="admin-login">
        <div className="bg-white p-8 shadow-lg max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <Lock size={48} className="mx-auto text-ocean mb-4" />
            <h1 className="font-heading text-3xl text-ocean">Admin Dashboard</h1>
            <p className="text-[#4A5D62] text-sm mt-2">Enter password to access</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="form-input-underline mb-6"
              data-testid="admin-password-input"
            />
            <button
              type="submit"
              className="w-full bg-[#0A2530] text-[#F4F1EA] hover:bg-[#163A48] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
              data-testid="admin-login-btn"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-[#F4F1EA]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-heading text-4xl text-ocean">Admin Dashboard</h1>
          <button
            onClick={() => setIsAuthenticated(false)}
            className="text-[#4A5D62] hover:text-coral text-sm"
          >
            Logout
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white p-6 shadow-sm">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">Room Inquiries</p>
            <p className="font-heading text-4xl text-ocean">{inquiries.room.length}</p>
          </div>
          <div className="bg-white p-6 shadow-sm">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">Activity Inquiries</p>
            <p className="font-heading text-4xl text-ocean">{inquiries.activity.length}</p>
          </div>
          <div className="bg-white p-6 shadow-sm">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">Contact Messages</p>
            <p className="font-heading text-4xl text-ocean">{inquiries.contact.length}</p>
          </div>
          <div className="bg-white p-6 shadow-sm">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A5D62] mb-2">Active Discounts</p>
            <p className="font-heading text-4xl text-coral">{discounts.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          {["room", "activity", "contact", "discounts"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-all ${
                activeTab === tab
                  ? "bg-[#0A2530] text-[#F4F1EA]"
                  : "border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA]"
              }`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab === "room" ? "Room Inquiries" : tab === "activity" ? "Activity Inquiries" : tab === "contact" ? "Contact Messages" : "Discounts"}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === "discounts" ? (
          <div className="bg-white p-6 shadow-sm">
            <h2 className="font-heading text-2xl text-ocean mb-6">Manage Discounts</h2>
            
            {/* Add Discount Form */}
            <form onSubmit={handleAddDiscount} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <input
                type="text"
                value={newDiscount.code}
                onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value.toUpperCase() })}
                placeholder="Discount Code"
                className="form-input-underline"
                data-testid="discount-code-input"
              />
              <input
                type="number"
                value={newDiscount.percentage}
                onChange={(e) => setNewDiscount({ ...newDiscount, percentage: e.target.value })}
                placeholder="Percentage (%)"
                min="1"
                max="100"
                className="form-input-underline"
                data-testid="discount-percentage-input"
              />
              <input
                type="date"
                value={newDiscount.validUntil}
                onChange={(e) => setNewDiscount({ ...newDiscount, validUntil: e.target.value })}
                className="form-input-underline"
                data-testid="discount-valid-until-input"
              />
              <button
                type="submit"
                className="bg-[#A85239] text-white hover:bg-[#8C432E] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-colors"
                data-testid="add-discount-btn"
              >
                Add Discount
              </button>
            </form>

            {/* Discounts List */}
            {discounts.length > 0 ? (
              <div className="space-y-4">
                {discounts.map((discount) => (
                  <div key={discount.id} className="flex items-center justify-between p-4 bg-[#F4F1EA]">
                    <div className="flex items-center gap-4">
                      <Tag size={24} className="text-coral" />
                      <div>
                        <p className="font-semibold text-ocean">{discount.code}</p>
                        <p className="text-sm text-[#4A5D62]">{discount.percentage}% off • Valid until {discount.validUntil || "No expiry"}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteDiscount(discount.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-discount-${discount.id}`}
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#4A5D62] text-center py-8">No discounts created yet.</p>
            )}
          </div>
        ) : (
          <div className="bg-white shadow-sm">
            <div className="flex justify-between items-center p-6 border-b border-[#DCD7CE]">
              <h2 className="font-heading text-2xl text-ocean">
                {activeTab === "room" ? "Room Inquiries" : activeTab === "activity" ? "Activity Inquiries" : "Contact Messages"}
              </h2>
              <button
                onClick={() => exportToCSV(inquiries[activeTab], `${activeTab}-inquiries`)}
                className="flex items-center gap-2 border border-[#0A2530] text-[#0A2530] hover:bg-[#0A2530] hover:text-[#F4F1EA] px-4 py-2 uppercase tracking-[0.15em] text-xs font-semibold transition-all"
                data-testid="export-csv-btn"
              >
                <Export size={16} /> Export CSV
              </button>
            </div>
            
            {loading ? (
              <p className="text-center py-12 text-[#4A5D62]">Loading...</p>
            ) : inquiries[activeTab].length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#EAE6DF]">
                    <tr>
                      <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Name</th>
                      <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Email</th>
                      <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">WhatsApp</th>
                      {activeTab === "room" && (
                        <>
                          <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Room</th>
                          <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Dates</th>
                        </>
                      )}
                      {activeTab === "activity" && (
                        <>
                          <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Activity</th>
                          <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Date</th>
                        </>
                      )}
                      {activeTab === "contact" && (
                        <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Subject</th>
                      )}
                      <th className="text-left p-4 text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries[activeTab].map((inquiry, index) => (
                      <tr key={inquiry.id || index} className="border-b border-[#EAE6DF] hover:bg-[#FAF9F6]">
                        <td className="p-4 text-ocean">{inquiry.name}</td>
                        <td className="p-4 text-[#4A5D62]">{inquiry.email}</td>
                        <td className="p-4 text-[#4A5D62]">{inquiry.phone || "-"}</td>
                        {activeTab === "room" && (
                          <>
                            <td className="p-4 text-[#4A5D62]">{inquiry.room_type}</td>
                            <td className="p-4 text-[#4A5D62]">{inquiry.check_in_date} to {inquiry.check_out_date}</td>
                          </>
                        )}
                        {activeTab === "activity" && (
                          <>
                            <td className="p-4 text-[#4A5D62]">{inquiry.activity_type}</td>
                            <td className="p-4 text-[#4A5D62]">{inquiry.preferred_date}</td>
                          </>
                        )}
                        {activeTab === "contact" && (
                          <td className="p-4 text-[#4A5D62]">{inquiry.subject}</td>
                        )}
                        <td className="p-4">
                          <button
                            onClick={() => setSelectedInquiry(inquiry)}
                            className="text-ocean hover:text-coral"
                            data-testid={`view-inquiry-${inquiry.id || index}`}
                          >
                            <Eye size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-12 text-[#4A5D62]">No inquiries yet.</p>
            )}
          </div>
        )}

        {/* Inquiry Detail Modal */}
        <AnimatePresence>
          {selectedInquiry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
              onClick={() => setSelectedInquiry(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white p-8 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-heading text-2xl text-ocean">Inquiry Details</h3>
                  <button onClick={() => setSelectedInquiry(null)} className="text-[#4A5D62] hover:text-ocean">
                    <X size={24} />
                  </button>
                </div>
                <div className="space-y-4">
                  {Object.entries(selectedInquiry).map(([key, value]) => (
                    key !== "id" && (
                      <div key={key}>
                        <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#4A5D62]">{key === "phone" ? "whatsapp number" : key.replace(/_/g, " ")}</p>
                        <p className="text-ocean">{value || "-"}</p>
                      </div>
                    )
                  ))}
                </div>
                <div className="mt-8">
                  {selectedInquiry.phone && (
                    <a
                      href={`https://wa.me/${selectedInquiry.phone.replace(/[^0-9]/g, '')}?text=Hi ${selectedInquiry.name}, thank you for your inquiry at Hippocampus Beach Resort! For any questions, you can also reach us at +63 917 137 8692.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full block bg-[#25D366] text-white hover:bg-[#128C7E] px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold transition-colors text-center"
                    >
                      Reply via WhatsApp
                    </a>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={
            <>
              <Navigation />
              <main>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/rooms" element={<RoomsPage />} />
                  <Route path="/activities" element={<ActivitiesPage />} />
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                </Routes>
              </main>
              <Footer />
            </>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
